import { del, post, put } from "@selector/shared";
import type { components } from "@selector/shared/openapi";
import { clsx } from "clsx";
import type { FormEvent } from "react";
import { use, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  ActionButtonIntent,
  AlertDialogComponent,
  Card,
  FormError,
  HStack,
  LoadingBar,
  Popover,
  Spacer,
  TextField,
  TriggerButton,
  TruncateText,
  VStack,
} from "widgets";
import { useOverlay, useVisuallyLoading } from "widgets/hooks";
import { sortAlpha } from "widgets/utils";

import { filterOutFrontendOnlyColumns } from "../utils";
import style from "./ColumnTemplate.module.css";
import { ColumnTemplatesContext } from "./context.ts";
import { collectColumnsFromGrid, getTemplateColumnIds } from "./utils";

export type TableRequest = components["schemas"]["TableRequest"];
type Template = components["schemas"]["TemplateResponse"];

export const ColumnTemplatesPanel = () => {
  const { tableSpecForTemplates } = use(ColumnTemplatesContext);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [forceReload, reload] = useReducer((n) => n + 1, 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const triggerReload = () => {
    setLoading(true);
    reload();
  };

  useEffect(() => {
    post("/getTemplates", undefined, undefined, tableSpecForTemplates as TableRequest)
      .then(({ data }) => {
        setError(null);
        setTemplates(data);
        setLoading(false);
      })
      .catch((error: Error) => {
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tableSpecForTemplates, forceReload]);

  const currentTemplate = templates.find((t) => t.active);
  const defaultTemplate = templates.find((t) => t.isDefault);
  const otherTemplates = templates.filter((t) => !t.active && !t.isDefault);

  return (
    <Card title="Column Templates" noBorder scrollable contentStyle={{ position: "relative" }}>
      <LoadingBar isLoading={loading} topPosition />
      {error && <FormError>{error}</FormError>}
      <VStack>
        {currentTemplate && <Template template={currentTemplate} />}
        {defaultTemplate && <Template template={defaultTemplate} />}
        <div style={{ marginTop: 5, marginBottom: 5, width: "100%", height: 1, background: "var(--ui-border)" }} />
        {otherTemplates
          .toSorted((t1, t2) => sortAlpha(t1.name, t2.name))
          .map((template) => (
            <Template template={template} key={template.id} reload={triggerReload} />
          ))}
        <NewTemplateInput reload={triggerReload} />
      </VStack>
    </Card>
  );
};

const Template = ({ template, reload }: { template: Template; reload?: VoidFunction }) => {
  const { currentColumnSelection, gridApi, setTemplateColumns, setColumnSelection } = use(ColumnTemplatesContext);

  const sanitizedTemplateColumns = useMemo(() => filterOutFrontendOnlyColumns(template.columns), [template.columns]);
  const templateColIds = getTemplateColumnIds(sanitizedTemplateColumns);

  const isActiveWithOrder =
    templateColIds.length === currentColumnSelection.length &&
    templateColIds.every((val, index) => val === currentColumnSelection[index]);

  const selectedColumnIds = new Set(currentColumnSelection);
  const isActiveWithoutOrder =
    templateColIds.length === currentColumnSelection.length &&
    templateColIds.every((colId) => selectedColumnIds.has(colId));

  const handlerReset = () => {
    if (!gridApi.current) {
      return;
    }

    // Need a new ref to make it reactive
    setTemplateColumns([...sanitizedTemplateColumns]);
    setColumnSelection(templateColIds);
  };

  return (
    <HStack key={template.id} className={clsx(style.template, { [style.active]: isActiveWithoutOrder })}>
      <TriggerButton
        icon="repartition"
        label={"Apply Template"}
        onPress={handlerReset}
        badge={isActiveWithoutOrder && !isActiveWithOrder}
      />
      <TruncateText text={template.name} style={{ position: "relative", top: 2 }} />
      {!!reload && (
        <>
          <Spacer />
          <HStack spacing={0}>
            <UpdateTemplateButton template={template} reload={reload} />
            <DeleteTemplateButton template={template} reload={reload} />
          </HStack>
        </>
      )}
    </HStack>
  );
};

const DeleteTemplateButton = ({ template, reload }: { template: Template; reload?: VoidFunction }) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { overlayProps, state, triggerProps } = useOverlay({
    onOpenChange(isOpen) {
      if (!isOpen) {
        setDeleting(false);
        setErrorMessage(undefined);
      }
    },
  });

  const handleDelete = () => {
    setDeleting(true);
    setErrorMessage(undefined);
    del("/template/{templateId}", { templateId: template.id })
      .then(() => {
        state.close();
        reload?.();
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  return (
    <>
      <TriggerButton
        {...triggerProps}
        icon="delete"
        label={"Delete Template"}
        isDisabled={state.isOpen || deleting}
        ref={triggerRef}
      />
      <Popover
        {...overlayProps}
        state={state}
        triggerRef={triggerRef}
        offset={5}
        placement="top"
        shouldCloseOnInteractOutside={() => !deleting}
      >
        <AlertDialogComponent
          title="Delete template?"
          closeFn={state.close}
          mainButton={{ label: "Delete", icon: "delete", onPress: handleDelete, isLoading: deleting }}
          errorCallout={errorMessage ? { message: errorMessage } : undefined}
        >
          Are you sure you want to delete
          <br />
          the template &quot;{template.name}&quot;?
        </AlertDialogComponent>
      </Popover>
    </>
  );
};

const UpdateTemplateButton = ({ template, reload }: { template: Template; reload?: VoidFunction }) => {
  const { tableSpecForTemplates, gridApi } = use(ColumnTemplatesContext);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [updating, setUpdating] = useState(false);
  const isVisuallyUpdating = useVisuallyLoading(updating);
  const { overlayProps, state, triggerProps } = useOverlay({
    onOpenChange(isOpen) {
      if (!isOpen) {
        setUpdating(false);
        setErrorMessage(undefined);
      }
    },
  });

  const handleUpdate = () => {
    if (!tableSpecForTemplates.resultType || !gridApi.current) {
      state.close();
      return;
    }

    setUpdating(true);
    setErrorMessage(undefined);

    const columns = collectColumnsFromGrid(gridApi.current);

    put("/template", undefined, undefined, {
      columns,
      id: template.id,
      resultType: tableSpecForTemplates.resultType,
      queryType: tableSpecForTemplates.queryType,
    })
      .then(() => {
        reload?.();
        state.close();
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setUpdating(false);
      });
  };

  return (
    <>
      <TriggerButton
        {...triggerProps}
        icon="publish"
        label={"Update Template"}
        isLoading={isVisuallyUpdating}
        ref={triggerRef}
      />
      <Popover
        {...overlayProps}
        state={state}
        triggerRef={triggerRef}
        offset={5}
        placement="top"
        shouldCloseOnInteractOutside={() => !updating}
      >
        <AlertDialogComponent
          title="Update template?"
          closeFn={state.close}
          mainButton={{ label: "Update", icon: "publish", onPress: handleUpdate, isLoading: updating }}
          errorCallout={errorMessage ? { message: errorMessage } : undefined}
          intent={ActionButtonIntent.Primary}
        >
          This will overwrite the template &quot;{template.name}&quot; with the
          <br />
          current column configuration. Continue?
        </AlertDialogComponent>
      </Popover>
    </>
  );
};

const NewTemplateInput = ({ reload }: { reload: VoidFunction }) => {
  const { gridApi, tableSpecForTemplates } = use(ColumnTemplatesContext);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  const [loading, setLoading] = useState(false);
  const trimmedName = name.trim();
  const isNameEmpty = !trimmedName;

  const handleNameChange = (value: string) => {
    if (error) {
      setError(undefined);
    }

    setName(value);
  };

  const handleSave = () => {
    if (!gridApi.current) {
      setError("Columns are still loading. Please try again.");
      return;
    }

    if (isNameEmpty) {
      setError("Please enter a template name.");
      return;
    }

    setError(undefined);
    setLoading(true);

    const columns = collectColumnsFromGrid(gridApi.current);

    post("/template", undefined, undefined, {
      columns,
      name: trimmedName,
      tableRequest: tableSpecForTemplates as components["schemas"]["TableRequest"],
    })
      .then(() => {
        reload();
      })
      .catch((error: Error) => {
        setError(error.message);
      })
      .finally(() => {
        setName("");
        setLoading(false);
      });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSave();
  };

  return (
    <VStack style={{ marginTop: 20 }}>
      <form onSubmit={handleSubmit}>
        <TextField
          label="New Column Template"
          name="newColumnTemplate"
          value={name}
          onChange={handleNameChange}
          isRequired
          action={
            <TriggerButton
              icon="add"
              label={"Save Template"}
              isLoading={loading}
              isDisabled={isNameEmpty}
              onPress={handleSave}
            />
          }
          errorMessage={error}
          isInvalid={!!error}
        />
      </form>
    </VStack>
  );
};
